import { Box, SimpleGrid, useToast } from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { SingleField } from '@duke-hq/ui/components/SingleField/SingleField';
import { djs } from '@duke-hq/djs';
import {
  getDisabilitiesOptions,
  getGenderOptions,
  getSchoolLevelOptions,
} from '../../constants/student';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useCallback, useState } from 'react';
import { AdditionalDetailsUpdateForm } from './AdditionalDetailsUpdateForm';
import { SectionHeading } from '../../components/SectionHeading';
import { StudentEducationLevel, StudentGenderKind } from '@duke-hq/svc-api-db';
import { countries } from 'countries-list';

function getCountryName(code: string): string {
  return countries[code]?.name ?? code;
}

export default function AdditionalDetailsView({
  studentId,
  dateOfBirth,
  gender,
  highestEducation,
  disabilities,
  countryOfBirth,
  isUsiVerified = false,
  shouldRefetchAvetmiss = false,
}: {
  studentId: string;
  dateOfBirth?: Date | string;
  gender?: StudentGenderKind;
  highestEducation?: StudentEducationLevel;
  disabilities?: string[];
  countryOfBirth?: string;
  isUsiVerified?: boolean;
  shouldRefetchAvetmiss?: boolean;
}): React.ReactElement {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const toast = useToast();
  const trpcUtils = trpcAdmin.useUtils();

  const updateAdditionalInfo = trpcAdmin.students.updateStudentAdditionalInfo.useMutation({
    onSuccess: async () => {
      toast({
        title: t`Student details updated successfully.`,
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
      setIsEditModalOpen(false);
      await trpcUtils.students.byId.refetch({ id: studentId });
    },
    onError: (error) => {
      toast({
        title: t`Error updating student details.`,
        description: error instanceof Error ? error.message : t`Failed to update student details`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    onSettled: () => {
      if (shouldRefetchAvetmiss) {
        void trpcUtils.avetmiss.sync.refetch();
      }
    },
  });

  const handleUpdate = useCallback(
    (values: {
      dateOfBirth?: Date;
      gender?: StudentGenderKind;
      highestEducation?: StudentEducationLevel;
      disabilities: string[];
      haveDisability: boolean;
      countryOfBirth?: string;
    }) => {
      updateAdditionalInfo.mutate({
        id: studentId,
        dateOfBirth: values.dateOfBirth,
        disabilities: values.disabilities,
        gender: values.gender,
        highestEducation: values.highestEducation,
        haveDisability: values.haveDisability,
        countryOfBirth: values.countryOfBirth,
      });
    },
    [studentId, updateAdditionalInfo],
  );

  return (
    <Box>
      <SectionHeading onEdit={() => setIsEditModalOpen(true)} editDetails={true}>
        <Trans>Additional Info</Trans>
      </SectionHeading>

      <SimpleGrid columns={2} w="full" spacing={4} mb={4}>
        <SingleField
          name={<Trans>Date of birth</Trans>}
          value={dateOfBirth ? djs(dateOfBirth).format('DD MMMM YYYY') : ''}
        />
        <SingleField
          name={<Trans>Gender</Trans>}
          value={getGenderOptions().find((option) => option.value === gender)?.label ?? gender}
        />
        <SingleField
          name={<Trans>Highest completed school level</Trans>}
          value={
            getSchoolLevelOptions().find((option) => option.value === highestEducation)?.label ??
            highestEducation
          }
        />
        <SingleField
          name={<Trans>Disability and special assistance</Trans>}
          value={
            disabilities
              ? disabilities
                  .map(
                    (disability) =>
                      getDisabilitiesOptions().find((option) => option.value === disability)
                        ?.label ?? disability,
                  )
                  .join(', ')
              : null
          }
        />
        <SingleField
          name={<Trans>Country of birth</Trans>}
          value={getCountryName(countryOfBirth ?? '')}
        />
      </SimpleGrid>

      <FormModalWrapper
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        modalContent={
          <AdditionalDetailsUpdateForm
            defaultDateOfBirth={dateOfBirth}
            defaultDisabilities={disabilities}
            defaultGender={gender}
            defaultHighestEducation={highestEducation}
            defaultCountryOfBirth={countryOfBirth}
            isUsiVerified={isUsiVerified}
            updateAdditionalInfo={handleUpdate}
            onCancel={() => setIsEditModalOpen(false)}
            isDataSubmitting={updateAdditionalInfo.isPending}
          />
        }
      />
    </Box>
  );
}
